import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Check, Mail, MapPin, Send } from "lucide-react";
import { toast, Toaster } from "sonner";
import axios from "axios";
import Footer from "../components/gridvest/Footer";
import SiteHeader from "../components/gridvest/SiteHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", subject: "", message: "" };

export default function ContactPage() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim() || null,
        message: form.message.trim(),
      });
      setDone(true);
      toast.success("Message sent. We'll be in touch shortly.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please review the fields.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07111F] text-[#F8FAFC]" data-testid="contact-page">
      <SiteHeader active="contact" />

      <main className="relative">
        <div className="gv-grid-bg absolute inset-x-0 top-0 h-[420px]" aria-hidden />

        <section className="relative mx-auto max-w-5xl px-6 pb-20 pt-16 lg:px-8 lg:pt-24">
          <Link
            to="/"
            className="mb-10 inline-flex items-center gap-2 font-body text-sm text-[#94A3B8] transition-colors hover:text-[#F8FAFC]"
            data-testid="contact-back-home"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-2xl"
          >
            <p className="mb-5 font-display text-[12px] font-extrabold uppercase tracking-[0.18em] text-[#38BDF8]">
              Contact Us
            </p>
            <h1 className="font-display text-4xl font-extrabold leading-[1] tracking-[-0.04em] text-[#F8FAFC] sm:text-5xl lg:text-[64px]">
              We'd like to hear from you.
            </h1>
            <p className="mt-6 font-body text-base leading-[1.8] text-[#CBD5E1] sm:text-lg">
              Investor inquiries, owner partnerships, municipal collaborations, or
              press — send a note and our team will respond.
            </p>
          </motion.div>

          <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12">
            {/* Form */}
            <motion.form
              onSubmit={handleSubmit}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="rounded-2xl border border-[#1E293B] bg-[#0F1B2D] p-6 sm:p-8 lg:col-span-7"
              data-testid="contact-form"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field label="Full name" required>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Jane Doe"
                    className={inputCls}
                    data-testid="contact-name"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="jane@company.com"
                    className={inputCls}
                    data-testid="contact-email"
                  />
                </Field>
                <Field label="Subject" className="sm:col-span-2">
                  <input
                    type="text"
                    value={form.subject}
                    onChange={(e) => update("subject", e.target.value)}
                    placeholder="Investor inquiry · Owner partnership · Municipal · Press"
                    className={inputCls}
                    data-testid="contact-subject"
                  />
                </Field>
                <Field label="Message" required className="sm:col-span-2">
                  <textarea
                    required
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="Share a few details about your inquiry."
                    rows={6}
                    className={inputCls + " resize-none"}
                    data-testid="contact-message"
                  />
                </Field>
              </div>

              <div className="mt-7 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                <p className="font-body text-xs text-[#64748B]">
                  By submitting, you agree we may contact you about InfraYield.
                </p>
                <button
                  type="submit"
                  disabled={submitting || done}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-[#22C55E] px-6 py-3.5 font-body text-sm font-semibold text-[#07111F] transition-colors hover:bg-[#22C55E]/90 disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#22C55E]/40"
                  data-testid="contact-submit"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : done ? (
                    <>
                      <Check className="h-4 w-4" />
                      Sent
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            </motion.form>

            {/* Sidebar */}
            <motion.aside
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="space-y-5 lg:col-span-5"
            >
              <InfoCard
                icon={Mail}
                title="Email"
                lines={[
                  { label: "General", value: "hello@infrayield.co", href: "mailto:hello@infrayield.co" },
                  { label: "Press", value: "press@infrayield.co", href: "mailto:press@infrayield.co" },
                  { label: "Accessibility", value: "accessibility@infrayield.co", href: "mailto:accessibility@infrayield.co" },
                ]}
              />
              <InfoCard
                icon={MapPin}
                title="Mailing"
                lines={[
                  { label: "Headquarters", value: "United States" },
                ]}
              />

              <div className="rounded-2xl border border-[#1E293B] bg-[#07111F]/60 p-5">
                <p className="font-display text-[11px] font-semibold uppercase tracking-[0.18em] text-[#38BDF8]">
                  Note
                </p>
                <p className="mt-2 font-body text-sm leading-relaxed text-[#94A3B8]">
                  Investment opportunities, if offered, will be made only through
                  compliant securities offerings and official offering documents.
                </p>
              </div>
            </motion.aside>
          </div>
        </section>
      </main>

      <Footer />
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: {
            background: "#0F1B2D",
            color: "#F8FAFC",
            border: "1px solid #1E293B",
          },
        }}
      />
    </div>
  );
}

const inputCls =
  "w-full rounded-md border border-[#1E293B] bg-[#07111F] px-4 py-3 font-body text-sm text-[#F8FAFC] placeholder:text-[#475569] focus:border-[#38BDF8] focus:outline-none focus:ring-1 focus:ring-[#38BDF8]";

function Field({ label, required, children, className = "" }) {
  return (
    <label className={"block " + className}>
      <span className="mb-2 inline-flex items-center gap-1.5 font-body text-xs font-medium uppercase tracking-[0.12em] text-[#94A3B8]">
        {label}
        {required && <span className="text-[#22C55E]">*</span>}
      </span>
      {children}
    </label>
  );
}

function InfoCard({ icon: Icon, title, lines }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-[#1E293B] bg-[#0F1B2D]">
      <div className="flex items-center gap-3 border-b border-[#1E293B] px-5 py-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-md border border-[#1E293B] bg-[#07111F] text-[#38BDF8]">
          <Icon className="h-4 w-4" />
        </div>
        <p className="font-display text-sm font-semibold text-[#F8FAFC]">{title}</p>
      </div>
      <ul className="divide-y divide-[#1E293B]">
        {lines.map((l) => (
          <li key={l.label + l.value} className="flex items-center justify-between gap-4 px-5 py-3.5">
            <span className="font-body text-xs uppercase tracking-[0.12em] text-[#94A3B8]">
              {l.label}
            </span>
            {l.href ? (
              <a
                href={l.href}
                className="font-body text-sm text-[#CBD5E1] transition-colors hover:text-[#F8FAFC]"
              >
                {l.value}
              </a>
            ) : (
              <span className="font-body text-sm text-[#CBD5E1]">{l.value}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Header() {
  return null;
}
